import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

const APK_DOWNLOAD_URL =
  "https://firebasestorage.googleapis.com/v0/b/sit-uce.firebasestorage.app/o/app%2FSIT-Platform.apk?alt=media&token=75b17303-341c-44bd-95d9-04dc70478623";

// ── Detect iOS ───────────────────────────────────────────────────────────────
const isIOS = () =>
  /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// ── Starfield ────────────────────────────────────────────────────────────────
function StarfieldBG() {
  const stars = [];
  for (let i = 0; i < 60; i++) {
    const cx = `${(Math.random() * 100).toFixed(1)}%`;
    const cy = `${(Math.random() * 100).toFixed(1)}%`;
    const r  = (Math.random() * 1.8 + 0.4).toFixed(2);
    const dur = (Math.random() * 6 + 4).toFixed(1);
    const delay = (Math.random() * 5).toFixed(1);
    const op  = (Math.random() * 0.5 + 0.15).toFixed(2);
    const fill = ["#3B82F6","#F59E0B","#60A5FA","#FBBF24","#93C5FD","#FDE68A","#fff"][Math.floor(Math.random()*7)];
    stars.push(
      <circle key={i} cx={cx} cy={cy} r={r} fill={fill} opacity={op}>
        <animate attributeName="opacity" values={`${op};${(op*0.2).toFixed(2)};${op}`} dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`} />
      </circle>
    );
  }
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div style={{ position:"absolute", top:"5%", left:"15%", width:600, height:600, background:"radial-gradient(circle,rgba(59,130,246,0.08) 0%,transparent 70%)", borderRadius:"50%" }} />
      <div style={{ position:"absolute", bottom:"10%", right:"10%", width:500, height:500, background:"radial-gradient(circle,rgba(245,158,11,0.06) 0%,transparent 70%)", borderRadius:"50%" }} />
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">{stars}</svg>
    </div>
  );
}

// ── Reveal on scroll ─────────────────────────────────────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}
function Reveal({ children, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <section ref={ref} className={`transition-all duration-700 ease-out ${className}`}
      style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(40px)" }}>
      {children}
    </section>
  );
}

// ── Divider ──────────────────────────────────────────────────────────────────
function Divider() {
  return (
    <div className="relative z-10 max-w-xl mx-auto px-6 py-2">
      <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
    </div>
  );
}

// ── Feature pill (sin emoji) ──────────────────────────────────────────────────
function Pill({ label, color = "#3DDC84", bg = "rgba(61,220,132,0.08)", border = "rgba(61,220,132,0.25)" }) {
  return (
    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border"
      style={{ background: bg, borderColor: border, color }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: color }} />
      {label}
    </span>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function DownloadApp() {
  const [heroReady, setHeroReady]       = useState(false);
  const [subtitleReady, setSubtitleReady] = useState(false);
  const [taglineReady, setTaglineReady] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setHeroReady(true), 200);
    const t2 = setTimeout(() => setSubtitleReady(true), 900);
    const t3 = setTimeout(() => setTaglineReady(true), 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  // Captura el evento de instalación PWA (Chrome, Edge, Android)
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setPwaInstalled(true));
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handlePWAInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setPwaInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="dl-page relative min-h-screen text-white overflow-x-hidden"
      style={{ background: "linear-gradient(180deg,#0a0a14 0%,#0d1024 30%,#0f1530 60%,#0a0a14 100%)" }}>
      <StarfieldBG />

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <h1 className="dl-hero-logo text-7xl sm:text-8xl md:text-9xl font-black tracking-wider select-none"
          style={{
            opacity: heroReady ? 1 : 0,
            transform: heroReady ? "translateY(0) scale(1)" : "translateY(30px) scale(0.9)",
            transition: "all 0.8s cubic-bezier(0.16,1,0.3,1)",
            background: "linear-gradient(135deg,#3B82F6,#60A5FA,#F59E0B,#FBBF24)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text",
          }}>SIT</h1>

        <p className="dl-subtitle mt-4 text-lg sm:text-xl md:text-2xl font-light tracking-widest text-blue-200"
          style={{ opacity: subtitleReady ? 1 : 0, transform: subtitleReady ? "translateY(0)" : "translateY(15px)", transition: "all 0.7s ease" }}>
          Social Interactive Technology
        </p>

        <p className="mt-6 text-base sm:text-lg md:text-xl max-w-lg leading-relaxed"
          style={{ opacity: taglineReady ? 1 : 0, transform: taglineReady ? "translateY(0)" : "translateY(15px)", transition: "all 0.7s ease", color: "rgba(255,255,255,0.7)" }}>
          Tu red social universitaria, ahora en tu bolsillo
        </p>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          style={{ opacity: taglineReady ? 1 : 0, transition: "opacity 0.6s ease 0.3s" }}>
          <span className="text-xs tracking-widest uppercase text-white/40">Descubre</span>
          <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center pt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-400 dl-scroll-dot" />
          </div>
        </div>
      </section>

      {/* ── ANDROID ───────────────────────────────────────────────────────── */}
      <Reveal className="relative z-10 py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-5 mb-6">
            <img src="/pagedownload/androidicono.png" alt="Android" className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-lg" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold" style={{ color: "#3DDC84" }}>Android</h2>
          </div>

          <p className="text-white/70 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mb-8">
            Descarga la app nativa de SIT para Android. Notificaciones en tiempo real,
            acceso instantáneo y la mejor experiencia.
          </p>

          <div className="flex flex-wrap gap-3 mb-10">
            <Pill label="Notificaciones push" />
            <Pill label="Acceso directo" />
            <Pill label="Rendimiento nativo" />
          </div>

          <a href={APK_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer"
            className="dl-apk-btn inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold text-black transition-transform duration-200 hover:scale-105 active:scale-95"
            style={{ background: "linear-gradient(135deg,#3DDC84,#2BB36B)" }}>
            <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
            </svg>
            Descargar APK
          </a>

          <p className="mt-4 text-sm text-white/40">Android 7.0 o superior &bull; 65 MB</p>
        </div>
      </Reveal>

      <Divider />

      {/* ── iPhone / iPad ─────────────────────────────────────────────────── */}
      <Reveal className="relative z-10 py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-5 mb-6">
            <img src="/pagedownload/iosicono.png" alt="iOS" className="w-16 h-16 md:w-20 md:h-20 object-contain drop-shadow-lg" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white">iPhone / iPad</h2>
          </div>

          <p className="text-white/70 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mb-4">
            Instala SIT como aplicación web progresiva (PWA) directamente desde Safari.
          </p>

          {/* Badge app nativa próximamente */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold mb-8"
            style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.3)", color: "#60A5FA" }}>
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-blue-400" />
            La app nativa para iOS estará disponible próximamente
          </div>

          {/* Botón directo PWA para iOS */}
          <div className="mb-10">
            <a href="https://sit-platform.com" target="_blank" rel="noopener noreferrer"
              className="dl-pwa-btn inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold text-white transition-transform duration-200 hover:scale-105 active:scale-95"
              style={{ background: "linear-gradient(135deg,#1D4ED8,#3B82F6)", boxShadow: "0 0 30px rgba(59,130,246,0.3), 0 4px 20px rgba(0,0,0,0.3)" }}>
              <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Abrir en Safari para instalar
            </a>
            <p className="mt-3 text-xs text-white/40 max-w-xs">
              Abre el link en Safari, luego sigue los pasos de abajo para agregar a tu pantalla de inicio.
            </p>
          </div>

          {/* Pasos */}
          <h3 className="text-base font-bold text-white/80 mb-4 uppercase tracking-widest">Cómo instalar</h3>
          <div className="space-y-4 mb-10">
            {[
              { step: 1, icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" /></svg>, title: "Abre sit-platform.com en Safari" },
              { step: 2, icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>, title: "Toca el botón Compartir (cuadro con flecha hacia arriba)" },
              { step: 3, icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>, title: "Selecciona 'Agregar a pantalla de inicio'" },
            ].map((s) => (
              <div key={s.step} className="flex items-center gap-4 p-4 rounded-xl"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                  style={{ background: "rgba(59,130,246,0.15)", color: "#60A5FA" }}>
                  {s.step}
                </div>
                <div className="flex items-center gap-3">
                  <span style={{ color: "#60A5FA" }}>{s.icon}</span>
                  <span className="text-white/80 text-sm sm:text-base">{s.title}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-3">
            <Pill label="Sin tienda de apps" color="#60A5FA" bg="rgba(59,130,246,0.08)" border="rgba(59,130,246,0.25)" />
            <Pill label="Actualizaciones automáticas" color="#60A5FA" bg="rgba(59,130,246,0.08)" border="rgba(59,130,246,0.25)" />
            <Pill label="Funciona como app nativa" color="#60A5FA" bg="rgba(59,130,246,0.08)" border="rgba(59,130,246,0.25)" />
          </div>
        </div>
      </Reveal>

      <Divider />

      {/* ── DESKTOP ───────────────────────────────────────────────────────── */}
      <Reveal className="relative z-10 py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-5 mb-6">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg,rgba(139,92,246,0.2),rgba(59,130,246,0.15))", border: "1px solid rgba(139,92,246,0.3)" }}>
              <svg className="w-9 h-9 md:w-11 md:h-11" fill="none" viewBox="0 0 24 24" stroke="#A78BFA" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold" style={{ color: "#A78BFA" }}>Escritorio</h2>
          </div>

          <p className="text-white/70 text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl mb-8">
            Instala SIT directamente en tu computador como una app de escritorio. Compatible con
            Windows, macOS y Linux a través de Chrome o Edge.
          </p>

          <div className="flex flex-wrap gap-3 mb-10">
            <Pill label="Windows / macOS / Linux" color="#A78BFA" bg="rgba(139,92,246,0.08)" border="rgba(139,92,246,0.25)" />
            <Pill label="Chrome y Edge" color="#A78BFA" bg="rgba(139,92,246,0.08)" border="rgba(139,92,246,0.25)" />
            <Pill label="Sin instalador" color="#A78BFA" bg="rgba(139,92,246,0.08)" border="rgba(139,92,246,0.25)" />
          </div>

          {/* Botón PWA (si el navegador lo soporta) o fallback a web */}
          {pwaInstalled ? (
            <div className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold"
              style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)", color: "#A78BFA" }}>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              App instalada correctamente
            </div>
          ) : deferredPrompt ? (
            <button onClick={handlePWAInstall}
              className="dl-desk-btn inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold text-white transition-transform duration-200 hover:scale-105 active:scale-95"
              style={{ background: "linear-gradient(135deg,#7C3AED,#A78BFA)", boxShadow: "0 0 30px rgba(139,92,246,0.3), 0 4px 20px rgba(0,0,0,0.3)" }}>
              <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" />
              </svg>
              Instalar como app de escritorio
            </button>
          ) : (
            <a href="https://sit-platform.com" target="_blank" rel="noopener noreferrer"
              className="dl-desk-btn inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-lg font-bold text-white transition-transform duration-200 hover:scale-105 active:scale-95"
              style={{ background: "linear-gradient(135deg,#7C3AED,#A78BFA)", boxShadow: "0 0 30px rgba(139,92,246,0.3), 0 4px 20px rgba(0,0,0,0.3)" }}>
              <svg className="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Abrir sit-platform.com
            </a>
          )}

          {!pwaInstalled && !deferredPrompt && (
            <p className="mt-3 text-xs text-white/40">
              Para instalar como app: abre en Chrome o Edge y busca el ícono de instalación en la barra de direcciones.
            </p>
          )}
        </div>
      </Reveal>

      <Divider />

      {/* ── PRÓXIMAMENTE ──────────────────────────────────────────────────── */}
      <Reveal className="relative z-10 py-20 md:py-28 px-6">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 bg-gradient-to-r from-blue-400 to-yellow-400 bg-clip-text text-transparent">
            Próximamente
          </h2>
          <p className="text-white/50 text-base sm:text-lg">
            Estamos trabajando para llevarte SIT a las tiendas oficiales
          </p>
        </div>

        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Google Play */}
          <div className="dl-store-card relative rounded-2xl p-6 sm:p-8 text-center overflow-hidden"
            style={{ background: "linear-gradient(145deg,rgba(61,220,132,0.06),rgba(255,255,255,0.02))", border: "1px solid rgba(61,220,132,0.15)" }}>
            <div className="dl-store-pulse absolute inset-0 rounded-2xl pointer-events-none" style={{ "--pulse-color": "rgba(61,220,132,0.08)" }} />
            <div className="relative z-10 flex flex-col items-center">
              <img src="/pagedownload/Google-Play-Logo.png" alt="Google Play" className="h-14 object-contain mb-5" />
              <h3 className="text-xl font-bold mb-1" style={{ color: "#3DDC84" }}>Google Play</h3>
              <p className="text-white/50 text-sm mb-5">Próximamente en Google Play Store</p>
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(61,220,132,0.1)", border: "1px solid rgba(61,220,132,0.2)", color: "#3DDC84" }}>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: "#3DDC84" }} />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "#3DDC84" }} />
                </span>
                En desarrollo
              </div>
            </div>
          </div>

          {/* App Store */}
          <div className="dl-store-card relative rounded-2xl p-6 sm:p-8 text-center overflow-hidden"
            style={{ background: "linear-gradient(145deg,rgba(59,130,246,0.06),rgba(255,255,255,0.02))", border: "1px solid rgba(59,130,246,0.15)" }}>
            <div className="dl-store-pulse absolute inset-0 rounded-2xl pointer-events-none" style={{ "--pulse-color": "rgba(59,130,246,0.08)" }} />
            <div className="relative z-10 flex flex-col items-center">
              <img src="/pagedownload/appstoreicono.png" alt="App Store" className="h-14 object-contain mb-5" />
              <h3 className="text-xl font-bold text-blue-400 mb-1">App Store</h3>
              <p className="text-white/50 text-sm mb-5">Próximamente en Apple App Store</p>
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)", color: "#60A5FA" }}>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-400" />
                </span>
                En desarrollo
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer className="relative z-10 py-12 px-6 text-center border-t border-white/5">
        <div className="max-w-3xl mx-auto space-y-3">
          <p className="text-2xl font-extrabold tracking-wider"
            style={{ background: "linear-gradient(135deg,#3B82F6,#F59E0B)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
            SIT
          </p>
          <p className="text-white/30 text-sm">&copy; 2025 SIT &mdash; Social Interactive Technology</p>
          <p className="text-white/40 text-sm">Hecho con <span className="text-red-400">&#10084;</span> para la comunidad universitaria</p>
          <Link to="/login" className="inline-block mt-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors">
            &larr; Volver al inicio
          </Link>
        </div>
      </footer>

      {/* ── ESTILOS ───────────────────────────────────────────────────────── */}
      <style>{`
        .dl-page { scroll-behavior: smooth; }

        .dl-hero-logo { animation: dlLogoGlow 4s ease-in-out infinite; }
        @keyframes dlLogoGlow {
          0%,100% { filter: drop-shadow(0 0 40px rgba(59,130,246,0.4)) drop-shadow(0 0 80px rgba(245,158,11,0.2)); }
          50%      { filter: drop-shadow(0 0 60px rgba(59,130,246,0.6)) drop-shadow(0 0 100px rgba(245,158,11,0.35)); }
        }

        .dl-subtitle { position: relative; }
        .dl-subtitle::after {
          content: ""; position: absolute; bottom: -4px; left: 50%; transform: translateX(-50%);
          width: 0; height: 2px; background: linear-gradient(90deg,#3B82F6,#F59E0B); border-radius: 2px;
          animation: dlUnderline 1s ease 1.4s forwards;
        }
        @keyframes dlUnderline { to { width: 100%; } }

        .dl-scroll-dot { animation: dlScrollBounce 2s ease-in-out infinite; }
        @keyframes dlScrollBounce {
          0%,100% { transform: translateY(0); opacity: 1; }
          50%      { transform: translateY(16px); opacity: 0.3; }
        }

        .dl-apk-btn { animation: dlApkGlow 2.5s ease-in-out infinite; }
        @keyframes dlApkGlow {
          0%,100% { box-shadow: 0 0 30px rgba(61,220,132,0.3), 0 4px 20px rgba(0,0,0,0.3); }
          50%      { box-shadow: 0 0 55px rgba(61,220,132,0.5), 0 0 80px rgba(61,220,132,0.2), 0 4px 20px rgba(0,0,0,0.3); }
        }

        .dl-pwa-btn { animation: dlPwaGlow 2.5s ease-in-out infinite; }
        @keyframes dlPwaGlow {
          0%,100% { box-shadow: 0 0 30px rgba(59,130,246,0.3), 0 4px 20px rgba(0,0,0,0.3); }
          50%      { box-shadow: 0 0 55px rgba(59,130,246,0.5), 0 0 80px rgba(59,130,246,0.2), 0 4px 20px rgba(0,0,0,0.3); }
        }

        .dl-desk-btn { animation: dlDeskGlow 2.5s ease-in-out infinite; }
        @keyframes dlDeskGlow {
          0%,100% { box-shadow: 0 0 30px rgba(139,92,246,0.3), 0 4px 20px rgba(0,0,0,0.3); }
          50%      { box-shadow: 0 0 55px rgba(139,92,246,0.5), 0 0 80px rgba(139,92,246,0.2), 0 4px 20px rgba(0,0,0,0.3); }
        }

        .dl-store-pulse { animation: dlStorePulse 3s ease-in-out infinite; }
        @keyframes dlStorePulse {
          0%,100% { box-shadow: inset 0 0 0px var(--pulse-color, rgba(255,255,255,0.05)); }
          50%      { box-shadow: inset 0 0 40px var(--pulse-color, rgba(255,255,255,0.05)); }
        }

        .dl-store-card { transition: transform 0.3s ease, border-color 0.3s ease; }
        .dl-store-card:hover { transform: translateY(-4px); border-color: rgba(255,255,255,0.15); }

        .dl-page ::selection { background: rgba(59,130,246,0.3); color: #fff; }
      `}</style>
    </div>
  );
}
